import React, {memo, useEffect} from 'react'
import {startCase, omitBy, isNil} from 'lodash'
import {Button, Dropdown, Grid, Icon, Table, Message, Menu} from 'semantic-ui-react'
import {
  withArchivesData,
  withErrorHandling,
  withPropsPresent,
  withNavigation
} from '../../hoc'
import {fromISOString, asOptions, getVisibleItemsByKey, toLocaleString, qs} from '../../util'
import {restoreArchive, getVersionById, getEnvironmentById, ignoreArchiveSuggestions} from '../../backend'
import {useSafeState} from '../../hook'
import {Paged} from '../shared'

const _Archives = (
  {
    suggested = false,
    menuSelection,
    archives,
    archiveNames,
    totalArchivesCount,
    location,
    navigate,
    query,
    ignoreArchiveSuggestions,
    getVersionById,
    getEnvironmentById
  }
) => {
  const [state, setState] = useSafeState({
    loading: '',
    ignoring: '',
    ignored: [],
    archiving: '',
    selectedNames: [],
    selectedTypes: [],
    totalArchivesCount
  })

  useEffect(() => {
    setState({
        ...state,
        loading: '',
        ignoring: '',
        archiving: '',
        selectedNames: getSelectedNames(query),
        selectedTypes: getSelectedTypes(query),
        totalArchivesCount
      }
    )
    let timeoutId
    if (!!query.restoreSucceeded) {
      timeoutId = setTimeout(() => {
        navigate(`/c/account/${menuSelection}`)
      }, 10000)
    }
    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId)
      }
    }
  }, [query])

  const getSelectedTypes = (query) => {
    const {selectedTypes} = query
    if (!selectedTypes) {
      return []
    }
    if (typeof selectedTypes === 'string') {
      return selectedTypes.split(',').map(t => t.toUpperCase())
    }
    return selectedTypes.map(t => t.toUpperCase())
  }

  const getSelectedNames = (query) => {
    const {selectedNames} = query
    if (!selectedNames) {
      return []
    }
    if (typeof selectedNames === 'string') {
      return selectedNames.split(',')
    }
    return selectedNames
  }

  const onRestore = async (archiveId) => {
    navigate(`/c/account/${menuSelection}/${archiveId}/restore`, {includeReturnPath: true})
  }

  const onView = async (archive) => {
    if (archive.type === 'SERVICE') {
      navigate(`/c/services/${archive.reference}/versions`, {includeReturnPath: true})
    }
    if (archive.type === 'VERSION') {
      setState({...state, loading: archive.id})
      const version = await getVersionById(archive.reference)
      navigate(`/c/services/${version.serviceId}/versions/${version.id}/environments`, {includeReturnPath: true})
    }
    if (archive.type === 'ENVIRONMENT') {
      setState({...state, loading: archive.id})
      const environment = await getEnvironmentById(archive.reference)
      navigate(`/c/services/${environment.serviceId}/versions/${environment.versionId}/environments/${environment.id}/resources`, {includeReturnPath: true})
    }
  }

  const onIgnore = async (archive) => {
    setState({...state, ignoring: archive.id})
    await ignoreArchiveSuggestions(archive)
    setState({
      ...state,
      ignoring: '',
      ignored: [...state.ignored, archive.id],
      totalArchivesCount: state.totalArchivesCount - 1
    })
  }

  const onArchive = async (archive) => {
    if (archive.type === 'SERVICE') {
      navigate(`/c/services/${archive.reference}/archive`, {includeReturnPath: true})
    }
    if (archive.type === 'VERSION') {
      setState({...state, archiving: archive.id})
      const version = await getVersionById(archive.reference)
      navigate(`/c/services/${version.serviceId}/versions/manage/${version.id}/archive`, {includeReturnPath: true})
    }
    if (archive.type === 'ENVIRONMENT') {
      setState({...state, archiving: archive.id})
      const environment = await getEnvironmentById(archive.reference)
      navigate(`/c/services/${environment.serviceId}/versions/${environment.versionId}/environments/${environment.id}/archive`, {includeReturnPath: true})
    }
  }

  const onFilterByTypes = (e, d) => {
    const query = {
      selectedNames: state.selectedNames,
      selectedTypes: d.value
    }
    // setState({...state, selectedTypes: d.value.map(v => v.toUpperCase())})
    navigate(`/c/account/${menuSelection}`, {query})
  }

  const onFilterByNames = (e, d) => {
    const query = {
      selectedNames: d.value,
      selectedTypes: state.selectedTypes
    }
    // setState({...state, selectedNames: d.value})
    navigate(`/c/account/${menuSelection}`, {query})
  }

  const onNavTo = (e, destination) => {
    e.preventDefault()
    navigate(destination)
  }

  const nameOptions = asOptions(archiveNames)
  const typeOptions = ['service', 'version', 'environment'].map((v) => {
    return {key: v.toUpperCase(), text: v, value: v.toUpperCase()}
  })
  let visibleArchives = getVisibleItemsByKey(state.selectedNames, archives, 'serviceName')
  if (state.selectedTypes.length) {
    visibleArchives = visibleArchives.filter(a => state.selectedTypes.includes(a.type))
  }
  if (state.ignored.length) {
    visibleArchives = visibleArchives.filter(a => !state.ignored.includes(a.id))
  }
  let noItemsMessage = ''
  if (!visibleArchives.length && !suggested) {
    noItemsMessage = (
      <div>No archives found. Archives will show here as you archive services, versions, and environments over
        time.</div>)
  }
  if (!visibleArchives.length && !!suggested) {
    noItemsMessage = (<div>No suggested archives found.</div>)
  }

  return (
    <Grid>
      <Grid.Row columns='16'>
        <Grid.Column width='16'>
          <Menu pointing secondary>
            <Menu.Item
              name='Archive Suggestions'
              active={menuSelection === 'suggested-archives'}
              onClick={(e) => onNavTo(e, '/c/account/suggested-archives')}
            />
            <Menu.Item
              name='Existing Archives'
              active={menuSelection === 'existing-archives'}
              onClick={(e) => onNavTo(e, '/c/account/existing-archives')}
            />
          </Menu>
        </Grid.Column>
      </Grid.Row>
      {
        suggested && visibleArchives.length > 0 && (
          <Grid.Row coloumns='16'>
            <Grid.Column width='16'>
              <Message size='small'>
                <Icon name='exclamation'/>
                The items below haven't been updated or used in the past 90 days.
              </Message>
            </Grid.Column>
          </Grid.Row>
        )
      }
      <Grid.Row columns='16'>
        <Grid.Column width='16'>
          <Grid>
            {
              !!query.restoreSucceeded && (
                <Grid.Row columns='16'>
                  <Grid.Column width='16'>
                    <Message positive>
                      <Message.Header>Successfully Submitted Restore Request</Message.Header>
                      <p>
                        Please give this 2-3 minutes to process. The archive will be removed after it is successfully
                        restored.
                      </p>
                    </Message>
                  </Grid.Column>
                </Grid.Row>
              )
            }
            <Grid.Row columns='16'>
              <Grid.Column width='6'>
                <Dropdown placeholder='Filter by name(s)' fluid multiple search selection
                          disabled={nameOptions.length === 0}
                          options={nameOptions}
                          value={state.selectedNames}
                          clearable
                          data-testid='archive-name-filter-dropdown'
                          onChange={onFilterByNames}/>
              </Grid.Column>
              <Grid.Column width='6'>
                <Dropdown placeholder='Filter by type(s)' fluid multiple search selection
                          disabled={typeOptions.length === 0}
                          options={typeOptions}
                          value={state.selectedTypes}
                          clearable
                          data-testid='archive-type-filter-dropdown'
                          onChange={onFilterByTypes}/>
              </Grid.Column>
            </Grid.Row>
            <Grid.Row columns='16'>
              <Grid.Column width='16'>
                <Paged navigate={navigate} location={location} items={visibleArchives} noItemsContent={noItemsMessage}
                       totalCount={state.totalArchivesCount} render={
                  ({page}) =>
                    <Table celled columns='16' data-testid='archives'>
                      <Table.Header>
                        <Table.Row>
                          {
                            !suggested && (
                              <Table.HeaderCell>Date</Table.HeaderCell>
                            )
                          }
                          <Table.HeaderCell>Type</Table.HeaderCell>
                          <Table.HeaderCell>Service</Table.HeaderCell>
                          <Table.HeaderCell>Version</Table.HeaderCell>
                          <Table.HeaderCell>Environment</Table.HeaderCell>
                          <Table.HeaderCell></Table.HeaderCell>
                        </Table.Row>
                      </Table.Header>
                      <Table.Body>
                        {
                          page.map(a => {
                            return (
                              <Table.Row key={a.id}>
                                {
                                  !suggested && (
                                    <Table.Cell
                                      data-test='archive-created'>{toLocaleString(fromISOString(a.createdAt))}</Table.Cell>
                                  )
                                }
                                <Table.Cell data-test='archive-type'>{startCase(a.type.toLowerCase())}</Table.Cell>
                                <Table.Cell data-test='archive-service'>{a.serviceName}</Table.Cell>
                                <Table.Cell data-test='archive-version'>{a.versionName || 'n/a'}</Table.Cell>
                                <Table.Cell
                                  data-test='archive-environment'>{a.environmentName || 'n/a'}</Table.Cell>
                                <Table.Cell data-test='archive-restore' width={suggested ? 2 : 1}>
                                  {
                                    !suggested && (
                                      <Button basic size='tiny'
                                              onClick={() => onRestore(a.id)}
                                              icon='undo'
                                              content='Restore'
                                              data-test='archive-restore-action'/>
                                    )
                                  }
                                  {
                                    suggested && (
                                      <>
                                        <Button basic size='tiny'
                                                onClick={() => onView(a)}
                                                loading={state.loading === a.id}
                                                icon='browser'
                                                content='View'
                                                data-test='archive-suggestion-view-action'/>
                                        <Button basic size='tiny'
                                                onClick={() => onIgnore(a)}
                                                loading={state.ignoring === a.id}
                                                icon='mute'
                                                content='Ignore'
                                                data-test='archive-suggestion-ignore-action'/>
                                        <Button basic size='tiny'
                                                onClick={() => onArchive(a)}
                                                loading={state.archiving === a.id}
                                                icon='archive'
                                                content='Archive'
                                                data-test='archive-suggestion-archive-action'/>
                                      </>
                                    )
                                  }
                                </Table.Cell>
                              </Table.Row>
                            )
                          })
                        }
                      </Table.Body>
                    </Table>
                }/>
              </Grid.Column>
            </Grid.Row>
          </Grid>
        </Grid.Column>
      </Grid.Row>
    </Grid>
  )
}

let Archives = (props) => {
  const {handleError} = props
  let menuSelection = 'suggested-archives'
  if (props.location.pathname.indexOf('/existing-archives') > -1) {
    menuSelection = 'existing-archives'
  }
  props = {
    ...props,
    menuSelection,
    ignoreArchiveSuggestions: handleError(ignoreArchiveSuggestions),
    restoreArchive: handleError(restoreArchive),
    getVersionById: handleError(getVersionById),
    getEnvironmentById: handleError(getEnvironmentById)
  }
  return (<_Archives {...props}/>)
}

Archives = withPropsPresent(Archives, ['archives'])
Archives = withArchivesData(Archives, 'Loading archives...')
Archives = withNavigation(Archives)
Archives = withErrorHandling(Archives)
Archives = memo(Archives)

export {Archives, _Archives}

export default Archives